import { useState, useEffect } from 'react';
import { Brain } from 'lucide-react';
import { isStopWord } from '../../../../../lib/stop-words';
import { useFilteredKeywords } from '../../../../../contexts/FilteredKeywordsContext';

interface KeywordsTabProps {
  keywords: any[];
  additionalKeywords: Set<string>;
  loading: boolean;
  error: string | null;
  onFilteredKeywordsChange?: (missing: string[], present: string[]) => void;
}

export function KeywordsTab({ keywords, additionalKeywords, loading, error, onFilteredKeywordsChange }: KeywordsTabProps) {
  const { setFilteredKeywords: setGlobalFilteredKeywords } = useFilteredKeywords();
  const [expandedSections, setExpandedSections] = useState<{
    missing: boolean;
    present: boolean;
    page: boolean;
  }>({
    missing: false,
    present: false,
    page: false
  });

  const [filteredKeywords, setLocalFilteredKeywords] = useState<any[]>([]);
  const [filteredPageKeywords, setFilteredPageKeywords] = useState<any[]>([]);

  useEffect(() => {
    async function filterKeywords() {
      // Filter keywords based on stored metrics
      const filtered: any[] = [];
      for (const k of keywords) {
        const isStop = await isStopWord(k.keyword);
        if (!isStop) {
          // Use stored presencePercentage and avgDensity
          if (k.presencePercentage >= 50 && k.avgDensity >= 0.3) {
            filtered.push(k);
          }
        }
      }
      setLocalFilteredKeywords(filtered);

      // Filter additional keywords
      const pageKws: any[] = [];
      for (const keyword of Array.from(additionalKeywords)) {
        const isStop = await isStopWord(keyword);
        if (!isStop) {
          pageKws.push({
            keyword,
            importance: 10,
            status: 'Page',
            density: 0,
            avgDensity: 0,
            densityRatio: 0,
            competitorsCount: 0,
            totalCompetitors: 0,
            isAdditional: true
          });
        }
      }
      setFilteredPageKeywords(pageKws);
    }

    filterKeywords();
  }, [keywords, additionalKeywords]);

  const missingKeywords = filteredKeywords.filter(k => k.status === 'Missing');
  const presentKeywords = filteredKeywords.filter(k => k.status === 'Present');

  // Передаємо відфільтровані ключі до AI components
  useEffect(() => {
    const missingWords = missingKeywords.map(k => k.keyword);
    const presentWords = presentKeywords.map(k => k.keyword);
    
    // Передаємо до RightSidebar callback
    if (onFilteredKeywordsChange) {
      onFilteredKeywordsChange(missingWords, presentWords);
    }
    
    // Передаємо в глобальний контекст для AIPage
    setGlobalFilteredKeywords(missingWords, presentWords);
  }, [missingKeywords, presentKeywords, onFilteredKeywordsChange, setGlobalFilteredKeywords]);

  const renderKeywordSection = (
    title: string, 
    keywords: any[], 
    type: 'missing' | 'present' | 'page'
  ) => {
    if (keywords.length === 0) return null;
    
    const isExpanded = expandedSections[type];
    const displayKeywords = isExpanded ? keywords : keywords.slice(0, 6);
    const hasMore = keywords.length > 6;

    let titleColor = '';
    let bgColor = '';
    let textColor = '';
    
    if (type === 'missing') {
      titleColor = 'text-red-700';
      bgColor = 'bg-red-50';
      textColor = 'text-red-700';
    } else if (type === 'present') {
      titleColor = 'text-green-700';
      bgColor = 'bg-green-50';
      textColor = 'text-green-700';
    } else if (type === 'page') {
      titleColor = 'text-blue-700';
      bgColor = 'bg-blue-50';
      textColor = 'text-blue-700';
    }

    return (
      <div className="mb-6">
        <h4 className={`text-sm font-medium mb-2 ${titleColor}`}>
          {title} ({keywords.length})
        </h4>
        <div 
          className="flex flex-wrap gap-1"
          data-section={`${type}-keywords`}
          data-keywords-container
        >
          {displayKeywords.map((word, index) => (
            <div 
              key={`${type}-${index}`} 
              className={`px-2 py-1 rounded-md text-xs font-medium ${bgColor} ${textColor} group relative`}
              data-keyword={word.keyword}
              title={type !== 'page' ? `Density: ${word.density.toFixed(2)}% / Avg: ${word.avgDensity.toFixed(2)}%` : 'SEO keyword'}
            >
              {word.keyword}
              {type !== 'page' && (
                <div className="absolute left-0 top-full z-20 bg-white rounded-md p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-md border border-gray-200 min-w-[200px] pointer-events-none">
                  <div className="text-xs text-gray-500">
                    <div>
                      <span>Density(our/avg): {word.density.toFixed(2)}%/{word.avgDensity.toFixed(2)}%</span>
                    </div>
                    {word.forms && word.forms.length > 1 && (
                      <div className="mt-1 truncate">
                        Forms: {word.forms.slice(0, 3).join(', ')}
                        {word.forms.length > 3 ? '...' : ''}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        
        {hasMore && (
          <button
            onClick={() => setExpandedSections({...expandedSections, [type]: !isExpanded})}
            className="mt-2 text-sm text-indigo-600 hover:text-indigo-800 flex items-center"
          >
            {isExpanded ? 'Show Less' : `Show More (${keywords.length - 6})`}
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-600 p-4">{error}</div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-gray-900">Keywords</h3>
      {renderKeywordSection("Missing Keywords", missingKeywords, "missing")}
      {renderKeywordSection("Present Keywords", presentKeywords, "present")}
      {renderKeywordSection("SEO Keywords", filteredPageKeywords, "page")}
      {filteredKeywords.length === 0 && filteredPageKeywords.length === 0 && (
        <div className="text-center text-gray-500 py-8">
          No important keywords available
        </div>
      )}
    </div>
  );
}